import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, User, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const ContactPage = () => {
  const { language } = useLanguage();
  const t = translations.contact;
  
  // Track if mobile view
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="pt-16 md:pt-20" data-testid="contact-page">
      {/* Hero Section - More compact on mobile */}
      <section className="bg-gradient-to-b from-primary/5 to-white py-6 md:py-12 lg:py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-noto-serif text-primary mb-2 md:mb-4">
              {t.pageTitle[language]}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-text-secondary leading-relaxed font-noto-sans">
              {t.pageSubtitle[language]}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section with Map */}
      <section className="py-6 md:py-12 lg:py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 md:gap-8 lg:gap-10">
              
              {/* Contact Information - Shows first on mobile */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-2 order-1 lg:order-2 space-y-4 md:space-y-6"
              >
                {/* Section Title */}
                <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold font-noto-serif text-primary">
                  {t.contactPerson[language]}
                </h2>
                
                {/* Primary Contact Card - 許小姐 */}
                <div
                  className="bg-white p-4 md:p-6 lg:p-8 rounded-xl shadow-md border border-gray-100"
                  data-testid="contact-person-0"
                >
                  {/* Contact Header */}
                  <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6 pb-3 md:pb-4 border-b border-gray-100">
                    <div className="p-2 md:p-3 bg-secondary/10 rounded-lg md:rounded-xl">
                      <User className="text-secondary" size={isMobile ? 20 : 24} />
                    </div>
                    <h3 className="text-lg md:text-xl lg:text-2xl font-semibold font-noto-serif text-primary">
                      {language === 'zh' ? '許小姐' : 'Ms. Hsu'}
                    </h3>
                  </div>
                  
                  {/* Contact Details - More tappable on mobile */}
                  <div className="space-y-3 md:space-y-4">
                    {/* Phone - Prominent tappable area */}
                    <a
                      href="tel:02-25421566"
                      className="flex items-center gap-3 md:gap-4 p-2 -mx-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors group"
                      data-testid="contact-phone"
                    >
                      <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/20 transition-colors">
                        <Phone size={isMobile ? 16 : 18} className="text-secondary" />
                      </div>
                      <span className="text-text-primary font-noto-sans text-base md:text-lg font-medium group-hover:text-secondary transition-colors">
                        02-25421566 #203
                      </span>
                    </a>
                    
                    {/* Email - Prominent tappable area */}
                    <a
                      href="mailto:ivy203@chengyangcpa.com.tw"
                      className="flex items-center gap-3 md:gap-4 p-2 -mx-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors group"
                      data-testid="contact-email"
                    >
                      <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/20 transition-colors">
                        <Mail size={isMobile ? 16 : 18} className="text-secondary" />
                      </div>
                      <span className="text-text-primary font-noto-sans text-sm md:text-base group-hover:text-secondary transition-colors break-all">
                        ivy203@chengyangcpa.com.tw
                      </span>
                    </a>
                    
                    {/* Address - Prominent tappable area with better wrapping */}
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=Cheng%20Yang%20%26%20Co.%2C%20CPAs%20Taipei%20Division&query_place_id=ChIJQSpaGJSpQjQRR_KB_aOFXM8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 md:gap-4 p-2 -mx-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors group pt-3 md:pt-4 mt-1 border-t border-gray-100"
                      data-testid="contact-address"
                    >
                      <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/20 transition-colors">
                        <MapPin size={isMobile ? 16 : 18} className="text-secondary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-text-primary font-noto-sans text-sm md:text-base leading-relaxed group-hover:text-secondary transition-colors block">
                          {language === 'zh' ? (
                            <>
                              <span className="inline">台北市中山區</span>
                              <span className="inline">長安東路一段27號9樓之1</span>
                            </>
                          ) : (
                            '9F-1, No. 27, Sec. 1, Chang\'an E. Rd., Zhongshan Dist., Taipei'
                          )}
                        </span>
                        <span className="text-xs text-secondary/70 mt-1 flex items-center gap-1">
                          <ExternalLink size={10} />
                          {language === 'zh' ? '點擊開啟地圖' : 'Open in Maps'}
                        </span>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Business Hours Card - Better visual weight */}
                <div className="bg-gradient-to-br from-secondary/10 to-accent/10 p-4 md:p-5 lg:p-6 rounded-xl border border-secondary/10">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="p-2.5 md:p-3 bg-white rounded-lg md:rounded-xl shadow-sm">
                      <Clock className="text-secondary" size={isMobile ? 18 : 22} />
                    </div>
                    <div>
                      <h3 className="text-base md:text-lg lg:text-xl font-semibold font-noto-serif text-primary mb-0.5">
                        {t.businessHours[language]}
                      </h3>
                      <p className="text-text-primary font-noto-sans text-sm md:text-base font-medium">{t.hours[language]}</p>
                      {language === 'zh' && (
                        <p className="text-text-secondary font-noto-sans text-xs md:text-sm mt-0.5">Mon – Fri 09:00–18:00</p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Map - Hidden on mobile, Shows on desktop */}
              {!isMobile && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="lg:col-span-3 order-2 lg:order-1"
                >
                  <div className="h-[400px] lg:h-full lg:min-h-[480px] rounded-xl overflow-hidden shadow-lg">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3614.463!2d121.5315!3d25.0497!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3442a96772e3a5e9%3A0x4a9c8c8c8c8c8c8c!2zMTA45Y-w5YyX5biC5Lit5bGx5Y2A6ZW35a6J5p2x6Lev5LiA5q61MjfomZ8!5e0!3m2!1szh-TW!2stw!4v1234567890123!5m2!1szh-TW!2stw&markers=color:red%7C25.0497,121.5315"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="誠揚聯合會計師事務所 台北分所"
                    ></iframe>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info - Shorter, cleaner closing */}
      <section className="py-6 md:py-10 lg:py-12 bg-background-paper">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <p className="text-sm md:text-base text-text-secondary font-noto-sans leading-relaxed">
              {t.additionalInfo[language]}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
